import React, { ReactNode } from 'react';
import { RootineImage } from './RootineImage';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import RootineButton from './RootineButton';
import { colors, theme } from '../utils/theme';

interface ImageAndTextButtonProps {
  url?: string;
  onClick?: () => void;
  label: string;
  ariaLabel: string;
  hideIcon?: boolean;
}

const styles = {
  dot: {
    width: 8,
    height: 8,
    marginRight: 16,
    marginLeft: 16,
    borderRadius: 50,
    background: colors.greys.BRAND_BLACK,
  },
};

export const ImageAndText = ({
  imgSrc,
  imgAlt,
  imgFit = 'cover',
  subtitle1,
  subtitle2,
  title,
  isTitleSecondaryColor = false,
  description,
  priceText,
  primaryButton,
  secondaryButton,
  reverseLayout = false,
}: {
  imgSrc: string;
  imgAlt: string;
  imgFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  subtitle1: string;
  subtitle2: string;
  title: string;
  isTitleSecondaryColor?: boolean;
  description?: ReactNode;
  priceText?: string;
  primaryButton: ImageAndTextButtonProps;
  secondaryButton?: ImageAndTextButtonProps;
  reverseLayout?: boolean;
}): JSX.Element => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Grid
      container
      flexDirection={
        isMobile
          ? reverseLayout
            ? 'column-reverse'
            : 'column'
          : reverseLayout
          ? 'row-reverse'
          : 'row'
      }
      alignItems={isMobile ? 'center' : 'start'}
      justifyContent="center"
      gap={isMobile ? 4 : 6}
    >
      <Grid item sm={12} md={5} lg={6}>
        <RootineImage src={imgSrc} alt={imgAlt} fit={imgFit} />
      </Grid>
      <Grid
        container
        item
        flexDirection="column"
        sx={{
          width: '100%',
          [theme.breakpoints.up('md')]: {
            width: '40%',
          },
        }}
      >
        <Grid container flexDirection="row" alignItems="center">
          <Typography fontSize={16}> {subtitle1.toUpperCase()}</Typography>
          <div style={styles.dot} />
          <Typography fontSize={16}>{subtitle2.toUpperCase()}</Typography>
        </Grid>
        <Typography
          variant="medium"
          sx={{
            mt: 4,
            mb: 7,
            color: isTitleSecondaryColor
              ? colors.blues.MID
              : colors.greys.BRAND_BLACK,
            fontSize: { xs: 32, md: 32, lg: 40 },
            [theme.breakpoints.up('laptop')]: {
              mb: description ? 0 : 12,
            },
          }}
        >
          {title}
        </Typography>

        {description && description}

        {priceText && (
          <Grid
            container
            flexDirection="row"
            alignItems="baseline"
            sx={{ mb: 5 }}
          >
            <Typography fontSize={32} sx={{ mr: 1.5 }}>
              {priceText}
            </Typography>

            <Typography variant="light" fontSize={24} sx={{ mr: 1.5 }}>
              Total
            </Typography>
          </Grid>
        )}
        <Grid
          container
          flexDirection="column"
          sx={{
            [theme.breakpoints.up('laptop')]: {
              width: '91%',
            },
          }}
        >
          <RootineButton
            url={primaryButton?.url}
            label={primaryButton.label}
            ariaLabel={primaryButton.ariaLabel}
            variation="primary"
            size="large"
            sx={{ mb: 1.5 }}
            hideIcon={primaryButton.hideIcon ?? false}
            as="a"
            openNewTab
          />
          {secondaryButton && (
            <RootineButton
              onClick={secondaryButton?.onClick}
              label={secondaryButton.label}
              ariaLabel={secondaryButton.ariaLabel}
              variation="outline_light"
              size="large"
              hideIcon={secondaryButton.hideIcon ?? true}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
